import React, { Fragment } from "react";
import {gql as gqlw} from "@apollo/client";
import {formatDistanceToNow, parseISO, differenceInMinutes} from "date-fns";
import {DateTime} from "../utils/DateTime";
import {diff, Diff} from "deep-diff";
import {Link} from "react-router-dom";
import {ActivityLogEntryFragment} from "../types/ActivityLogEntryFragment";
import styles from "./AnnotationLogEntry.module.css";
import {
  WordLinkWithDetails,
  WordWithDetailsFragment
} from "../utils/WordWithDetails";
import ReactMarkdown from "react-markdown";

export const activityLogEntryFragment = gqlw`
  ${WordWithDetailsFragment}
  fragment ActivityLogEntryFragment on LogEntry {
    id,
    timestamp,
    eventType,
    actingUser { id, username },
    data,
    
    word {
      id,
      base
      ...WordWithDetails
    }
  }
`;

type TitleOpts = {
  hideWord?: boolean;
};

/**
 * This renders a single entry.
 */
export function ActivityLogEntry(props: {
  logEntry: ActivityLogEntryFragment;
  onUserClick?: (userId: number) => void;
}) {
  const {logEntry: entry} = props;

  let renderFunc: (data: any) => any =
    RenderFuncs[entry.eventType!] || renderUnknownEntry;
  let renderTitleFunc: (data: any) => any =
    RenderTitleFuncs[entry.eventType!] || renderUnknownTitle;

  return (
    <div style={{margin: "20px 0"}}>
      <div>
        <strong>{formatDistanceToNow(parseISO(entry.timestamp))} ago</strong> on
        the{" "}
        <strong>
          <DateTime date={entry.timestamp} />
        </strong>{" "}
        by{" "}
        <strong
          onClick={() => {
            if (!props.onUserClick) {
              return;
            }
            props.onUserClick(parseInt(entry?.actingUser?.id ?? ""));
          }}
        >
          {entry.actingUser
            ? entry.actingUser.username || entry.actingUser.id
            : "unknown"}
        </strong>
        : {renderTitleFunc(entry)}
      </div>

      {renderFunc(entry.data)}
    </div>
  );
}

type RenderTitleFunc = (entry: any, opts?: TitleOpts) => any;
const RenderTitleFuncs: {[name: string]: RenderTitleFunc} = {
  word_created: renderWordTitle,
  word_changed: renderWordTitle,
  word_approved: renderWordTitle,
  word_unapproved: renderWordTitle,
  word_deleted: renderWordTitle,
  note_created: renderWordTitle,
  meaning_created: renderWordTitle,
  meaning_definition_created: renderWordTitle,
  meaning_definition_changed: renderWordTitle
};

function renderUnknownTitle(entry: any) {
  return <strong>{entry.eventType}</strong>;
}

function renderWordTitle(entry: ActivityLogEntryFragment, opts?: TitleOpts) {
  const word = entry.word;

  let parts = [];

  parts.push(<strong key={"type"}>{eventLabel(entry.eventType)}</strong>);

  if (!opts?.hideWord) {
    let link;
    if (word) {
      link = <WordLinkWithDetails {...word} hideReviewedMark={true} />;
    } else {
      const data = parseLogData(entry.data);
      link = (
        <Link to={`/word/${data.word_id}`}>
          <span>now deleted word</span>
        </Link>
      );
    }
    parts.push(<Fragment key={"post"}> for {link}</Fragment>);
  }

  return <span>{parts}</span>;
}

type RenderFunc = (data: any) => any;
const RenderFuncs: {[name: string]: RenderFunc} = {
  word_created: renderWithoutData,
  word_changed: renderWordChanged,
  word_approved: renderWithoutData,
  word_unapproved: renderWithoutData,
  word_deleted: renderWithoutData,
  note_created: renderNoteCreated,
  meaning_created: renderMeaningCreated,
  meaning_definition_created: renderMeaningDefinitionCreated,
  meaning_definition_changed: renderMeaningDefinitionChanged
};

//<pre>{JSON.stringify(JSON.parse(data), null, 4)}</pre>;

function renderWithoutData(data: any) {
  return null;
}

function renderNoteCreated(data: string) {
  const parsed = parseLogData(data);
  const note = parseJsonField(parsed.note) || {};
  return <ReactMarkdown className={styles.note}>{note.text}</ReactMarkdown>;
}

function renderMeaningCreated(data: any) {
  const parsed = parseLogData(data);
  const meaning = parseJsonField(parsed.meaning) || {};
  const descriptor = meaning.descriptor || parsed.descriptor || "unnamed meaning";

  return (
    <div className={styles.logDetail}>
      Created meaning <strong>{descriptor}</strong>{" "}
      <span className={styles.muted}>#{parsed.meaning_id || meaning.id}</span>
      {renderLogMeta(parsed, meaning)}
    </div>
  );
}

function renderMeaningDefinitionCreated(data: any) {
  const parsed = parseLogData(data);
  const definition = parseJsonField(parsed.definition) || {};
  return (
    <div className={styles.logDetail}>
      Added {renderDefinitionSummary(definition)}
      {renderLogMeta(parsed, definition)}
    </div>
  );
}

function renderMeaningDefinitionChanged(data: any) {
  const parsed = parseLogData(data);
  const oldDefinition = parseJsonField(parsed.old) || {};
  const newDefinition = parseJsonField(parsed.new) || {};
  const changes = diff(oldDefinition, newDefinition);

  return (
    <div className={styles.logDetail}>
      Changed {renderDefinitionSummary(newDefinition)}
      {changes ? (
        <ul className={styles.changeList}>
          {changes.map((change, idx) => {
            return <li key={idx}>{renderDiffChange(change)}</li>;
          })}
        </ul>
      ) : null}
    </div>
  );
}

function renderDefinitionSummary(definition: any) {
  const data = parseJsonField(definition.data) || {};
  const words = Array.isArray(data.words) ? data.words : [];
  const wordText = words.length ? words.join(", ") : data.text || "definition";

  return (
    <span>
      <span className={styles.lang}>{definition.lang || "?"}</span>{" "}
      definition <strong>{wordText}</strong>
      {data.text && words.length ? <span className={styles.definitionText}> — {data.text}</span> : null}
    </span>
  );
}

function renderLogMeta(logData: any, serializedObject?: any) {
  const meta = serializedObject?.data?.meta || logData || {};
  const origin = logData.origin || meta.origin;
  const model = logData.model || meta.model;
  const runId = logData.run_id || meta.run_id;
  const promptVersion = logData.prompt_version || meta.prompt_version;

  if (!origin && !model && !runId && !promptVersion) {
    return null;
  }

  return (
    <span className={styles.meta}>
      {origin ? <span>origin: {origin}</span> : null}
      {model ? <span>model: {model}</span> : null}
      {runId ? <span>run: {runId}</span> : null}
      {promptVersion ? <span>prompt: {promptVersion}</span> : null}
    </span>
  );
}

function renderWordChanged(data: any) {
  const parsed = parseLogData(data);
  const changes = diff(maybeJson(parsed.old), maybeJson(parsed.new), {
    normalize: (path, key, lhs, rhs) => {
      const fullpath = [...path, key].join("/");
      if (
        ["base_translit", "stems", "data/primary", "data/sounds"].indexOf(
          fullpath
        ) > -1
      ) {
        return [lhs?.join(", ") ?? "", rhs?.join(", ") ?? ""];
      }

      return [lhs, rhs];
    }
  });
  if (!changes) {
    return null;
  }
  return (
    <div>
      {/*<pre>{JSON.stringify(maybeJson(parsed.old), null, 4)}</pre>*/}
      {/*<pre>{JSON.stringify(maybeJson(parsed.new), null, 4)}</pre>*/}
      {changes.map((change, idx) => {
        return <li key={idx}>{renderDiffChange(change)}</li>;
      })}
    </div>
  );
}

function renderDiffChange(change: Diff<any, any>) {
  const path = change?.path?.join("/");

  // property/element was edited
  if (change.kind === "E") {
    return (
      <span>
        Changed <strong>{path}</strong> from{" "}
        <strong>{JSON.stringify(change.lhs)}</strong> to{" "}
        <strong>{JSON.stringify(change.rhs)}</strong>.
      </span>
    );
  }

  // newly added property/element
  if (change.kind === "N") {
    return (
      <span>
        Added <strong>{JSON.stringify(change.rhs)}</strong> to{" "}
        <strong>{path}</strong>.
      </span>
    );
  }

  // deleted property/element
  if (change.kind === "D") {
    return (
      <span>
        Removed <strong>{JSON.stringify(change.lhs)}</strong> from{" "}
        <strong>{path}</strong>.
      </span>
    );
  }

  return <pre>{JSON.stringify(change, null, 4)}</pre>;
}

function renderUnknownEntry(data: any) {
  return <div style={{fontStyle: "italic"}}>This log event is unknown.</div>;
}

function parseLogData(data: any): any {
  if (!data) {
    return {};
  }
  if (typeof data == "string") {
    try {
      return JSON.parse(data);
    } catch (err) {
      return {raw: data};
    }
  }
  return data;
}

function parseJsonField(data: any): any {
  if (!data) {
    return data;
  }
  if (typeof data == "string") {
    try {
      return JSON.parse(data);
    } catch (err) {
      return {raw: data};
    }
  }
  return data;
}

function maybeJson(data: any): any {
  return parseJsonField(data);
}

function eventLabel(eventType?: string | null) {
  const labels: {[name: string]: string} = {
    word_created: "created word",
    word_changed: "changed word",
    word_approved: "approved word",
    word_unapproved: "unapproved word",
    word_deleted: "deleted word",
    note_created: "added note",
    meaning_created: "created meaning",
    meaning_definition_created: "added meaning definition",
    meaning_definition_changed: "changed meaning definition"
  };
  return (eventType && labels[eventType]) || eventType;
}

type GroupedEntry = {
  user: string | undefined;
  time: Date;
  groupedEntries: ActivityLogEntryFragment[];
};

/**
 * Collapses log entries by the same acting user around the same time.
 */
export type LogEntry = {
  actingUser: {id: string};
  timestamp: string;
};
export function groupLogEntriesByUserAndTime(
  entries: ActivityLogEntryFragment[]
) {
  const result: GroupedEntry[] = [];

  for (const entry of entries) {
    let currentGroup = result[result.length - 1];

    const timeOfEntry = parseISO(entry.timestamp);

    // Add a new group?
    let addNewGroup = false;
    if (!currentGroup) {
      addNewGroup = true;
    } else {
      addNewGroup =
        currentGroup.user != entry.actingUser?.id ||
        differenceInMinutes(currentGroup.time, timeOfEntry) > 15;
    }

    if (addNewGroup) {
      currentGroup = {
        user: entry.actingUser?.id,
        time: timeOfEntry,
        groupedEntries: []
      };
      result.push(currentGroup);
    }

    currentGroup.groupedEntries.push(entry);
  }

  return result;
}

/**
 * This renders a grouped entry.
 */
export function ActivityLogGroupedEntry(props: {
  entry: GroupedEntry;
  onUserClick?: (userId: number) => void;
  hideTitle?: boolean;
}) {
  const {entry} = props;

  const actingUser = entry.groupedEntries[0].actingUser;

  return (
    <div style={{margin: "20px 0"}} className={"group"}>
      <div className={styles.groupTitle}>
        <strong>{formatDistanceToNow(entry.time)} ago</strong> on the{" "}
        <strong>
          <DateTime date={entry.time} />
        </strong>{" "}
        by{" "}
        <strong
          onClick={() => {
            if (!props.onUserClick) {
              return;
            }
            props.onUserClick(parseInt(actingUser?.id ?? ""));
          }}
        >
          {actingUser ? actingUser.username || actingUser.id : "unknown"}
        </strong>
        :{" "}
      </div>

      {entry.groupedEntries.map(e => {
        let renderFunc: (data: any) => any =
          RenderFuncs[e.eventType!] || renderUnknownEntry;
        let renderTitleFunc: RenderTitleFunc =
          RenderTitleFuncs[e.eventType!] || renderUnknownTitle;

        return (
          <div key={e.id!} className={styles.groupEntry}>
            {renderTitleFunc(e, {hideWord: props.hideTitle})}
            {renderFunc(e.data)}
          </div>
        );
      })}
    </div>
  );
}
